import { useState, useEffect, useRef } from "react";
import Icon from "../Icon";
import { colorJornada } from "../../utils";
import { hhmm, hhmmss } from "../../utils/hora";

// Indicador de fichaje en la cabecera: punto verde si estás dentro, rojo si no.
//
// Recibe el estado de useFichaje desde App para compartirlo con el widget del
// panel; si cada uno tuviera su propio hook, fichar en uno dejaría al otro
// desactualizado.
//
// El clic NO ficha directamente: abre una confirmación. En la cabecera está
// siempre visible y un roce accidental crearía un fichaje real que luego habría
// que corregir por el flujo de solicitudes de Odoo.

// En miniatura el tiempo va en HORAS Y MINUTOS, igual que el cronometro de al
// lado: dos contadores contiguos con formatos distintos se leen mal. Los
// segundos siguen en el title y en el widget del panel de inicio.
const corto = ms => {
  const min = Math.max(0, Math.floor(ms / 60000));
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
};

export default function FichajeHeaderButton({ fichaje }) {
  const [abierto, setAbierto] = useState(false);
  const [ahora, setAhora] = useState(Date.now());
  const ref = useRef(null);
  const boton = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    if (fichaje.estado !== "dentro") return;
    // Cada 15 s basta: el contador va en minutos y repintar la cabecera entera
    // una vez por segundo era trabajo para no cambiar nada en pantalla.
    const id = setInterval(() => setAhora(Date.now()), 15000);
    return () => clearInterval(id);
  }, [fichaje.estado]);

  // Cerrar al pulsar fuera
  useEffect(() => {
    if (!abierto) return;
    const fuera = e => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    // Escape cierra y devuelve el foco al boton; al abrir, el foco entra en el panel.
    const tecla = e => { if (e.key === "Escape") { setAbierto(false); boton.current?.focus(); } };
    panel.current?.querySelector("button:not(:disabled)")?.focus();
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", tecla); };
  }, [abierto]);

  // OJO con el orden: todos los hooks van ANTES del return temprano de mas
  // abajo. Colocar un useEffect despues cambia el numero de hooks entre renders
  // y React lanza el error 310.

  const dentro = fichaje.estado === "dentro";

  // El contador de la cabecera muestra el TOTAL del día, no solo el tramo en
  // curso: con los descansos fichados como salida + entrada, la jornada son
  // varios tramos y el tramo actual solo cuenta el último trozo.
  //
  // Odoo da worked_hours = 0 mientras el tramo está abierto, así que el tramo
  // en curso se suma aparte con el reloj del navegador.
  const cerradas = (fichaje.hoy || [])
    .filter(t => t.check_out)
    .reduce((s, t) => s + (t.horas || 0), 0) * 3600000;
  const enCurso = dentro && fichaje.desde ? ahora - new Date(fichaje.desde).getTime() : 0;
  const totalDia = cerradas + enCurso;

  // El aviso de las nueve horas se subio a App.jsx: este indicador ya no se
  // pinta en Inicio —alli esta el widget grande—, y con el escondido el aviso no
  // saltaba justo a quien se deja el panel abierto toda la tarde. Un aviso de fin
  // de jornada no puede depender de en que pagina estes.

  // Sin conectar no se pinta nada: el widget del panel es donde se conecta.
  if (!fichaje.conectado) return null;

  // Dos colores para dos cosas distintas, que estaban mezcladas:
  //   estado -> verde si estas dentro, rojo si estas fuera
  //   accion -> rojo para fichar salida, verde para fichar entrada
  // Al usar el mismo, el boton de salida salia verde aqui y rojo en el panel.
  const colorEstado = dentro ? "var(--bar-ok)" : "var(--danger)";
  const colorAccion = dentro ? "var(--danger)" : "var(--ok-solid)";
  const colorSobreAccion = dentro ? "var(--on-danger)" : "var(--on-ok)";

  // El color del contador va sobre el TOTAL del dia, no sobre el tramo: si no,
  // salir a comer devolveria el contador a rojo con seis horas trabajadas.
  const colorHoras = colorJornada(totalDia);

  const confirmar = async () => { setAbierto(false); await fichaje.fichar(); };

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={boton} className="btn" onClick={() => setAbierto(p => !p)}
        aria-expanded={abierto} aria-haspopup="dialog"
        aria-label={dentro ? `Fichaje: dentro desde las ${hhmm(fichaje.desde, "--:--")}, ${corto(totalDia)} hoy`
                           : `Fichaje: fuera de jornada, ${corto(totalDia)} hoy`}
        title={dentro ? `Dentro desde las ${hhmm(fichaje.desde, "--:--")} · ${hhmmss(totalDia)} hoy` : `Fuera de jornada · ${hhmmss(totalDia)} hoy`}
        style={{
          padding: "6px 12px", borderRadius: 8, border: `1px solid ${colorEstado}55`,
          background: abierto ? `${colorEstado}1f` : "var(--surface)",
          color: "var(--text2)", cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
          fontSize: 11.5, fontFamily: "var(--font)",
        }}>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: colorEstado, flexShrink: 0,
                       boxShadow: `0 0 6px ${colorEstado}` }}/>
        <span className="m-hide" style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", color: colorHoras }}>
          {corto(totalDia)}
        </span>
      </button>

      {abierto && (
        <div ref={panel} role="dialog" aria-label="Fichar entrada o salida" style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 60, minWidth: 208,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 11,
          boxShadow: "var(--shadow-lg)", padding: 12,
          display: "flex", flexDirection: "column", gap: 9,
        }}>
          <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.7 }}>
            {dentro
              ? <>Dentro desde las <b style={{ color: "var(--text)" }}>{hhmm(fichaje.desde, "--:--")}</b><br/>
                  Tramo actual <b style={{ color: "var(--text)" }}>{corto(enCurso)}</b></>
              : "No has fichado entrada"}
            {(fichaje.hoy || []).length > 0 && (
              <><br/>Hoy en total <b style={{ color: colorHoras }}>{corto(totalDia)}</b>
                {" "}en {fichaje.hoy.length} tramo{fichaje.hoy.length === 1 ? "" : "s"}</>
            )}
          </div>

          <button
            onClick={confirmar} disabled={fichaje.fichando}
            style={{
              padding: "9px 0", borderRadius: 8, border: "none", width: "100%",
              cursor: fichaje.fichando ? "wait" : "pointer", fontSize: 12.5, fontWeight: 700,
              color: colorSobreAccion, background: colorAccion, opacity: fichaje.fichando ? .6 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              fontFamily: "var(--font)",
            }}>
            <Icon name={dentro ? "box-arrow-right" : "box-arrow-in-right"} size={14} color={colorSobreAccion}/>
            {fichaje.fichando ? "Registrando…" : dentro ? "Fichar salida" : "Fichar entrada"}
          </button>

        </div>
      )}
    </div>
  );
}
